/**
 * The dashboard's category accents, and the hues a person's avatar can be.
 *
 * A dashboard card is recoloured to say *what it is about* — revenue, a live
 * session, a streak, a series — and that colour is part of the card's identity.
 * The app has semantic roles for three of those meanings and no role at all for
 * the rest: there is no chrome role for "teal", and inventing one would be worse
 * than declaring the hue, because a role that means nothing is a colour that
 * cannot be reviewed.
 *
 * So this is the same shape as `slideTokens.ts`: **a palette that is the data**.
 * A screen imports `DASHBOARD_ACCENT.live` and reads `live`, and the value is
 * declared exactly once — which is what lets `no-colour-literals` be enforced on
 * the screens themselves. The alternative, a hex inlined at each of the forty-odd
 * places these are used, is how a palette drifts.
 *
 * What belongs on the chrome instead, and is used from there: `success`,
 * `danger`, `warning` and `accentFill` where a card is expressing *meaning*
 * rather than category, and `onAccent` for ink sitting on an accent fill. Those
 * are the ones that genuinely have to follow Settings → Appearance.
 */

export const DASHBOARD_ACCENT = {
  /** Money earned, sessions completed, a target met — the "this is good" hue. */
  positive: '#22c55e',
  /** Platform-wide figures, and the default series in a chart. */
  primary: '#0085ff',
  /** Something happening now: a live session, live revenue. */
  live: '#a855f7',
  /** Practice and engagement: challenges, capsules, streaks. */
  engagement: '#14b8a6',
  /** Time and attention: how long a streak is, what is due next. */
  attention: '#f59e0b',
  /** A second warm series, so a legend can carry two of them apart. */
  series: '#f97316',
  /** Social and celebratory: rankings, shout-outs, milestones. */
  celebration: '#ec4899',
} as const

/**
 * The hues a person's avatar can be given, in a fixed order.
 *
 * Identity rather than status: two students do not differ in *quality* because
 * their avatars differ, so these are deliberately not the semantic colours the
 * app reserves for meaning. The order is stable so the same person keeps the
 * same colour between sessions — which is also why the list is here and not
 * derived from the map above.
 */
export const AVATAR_HUES: readonly string[] = [
  DASHBOARD_ACCENT.positive,
  DASHBOARD_ACCENT.primary,
  DASHBOARD_ACCENT.live,
  DASHBOARD_ACCENT.attention,
  DASHBOARD_ACCENT.engagement,
  DASHBOARD_ACCENT.series,
  DASHBOARD_ACCENT.celebration,
]

/**
 * The avatar hue for a key that should keep the same colour forever.
 *
 * A hash rather than an index, so the colour survives a list being reordered or
 * filtered — a person is the same colour on every screen that shows them.
 */
export function avatarHueFor(key: string): string {
  // The arithmetic is deliberately the long-standing one, `>>> 0` included: it
  // decides which hue every existing account already shows, and changing it
  // would recolour people for no reason.
  let hash = 0
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0
  }
  return AVATAR_HUES[hash % AVATAR_HUES.length]
}
